import React from "react";
import {
  useGetSnapshotEventGroupedQuery,
  useGetSnapshotEventGroupedFiltersQuery,
} from "@/modules/snapshots/businessReports.api";
import {
  GetSnapshotEventGroupedResponseItem,
  WITHOUT_EVENT_FILTER_KEY,
} from "@/modules/snapshots/businessReports.types";
import { FilterBar } from "./components/FilterBar";
import { TimelineChart } from "./components/TimelineChart";
import { StatusPieChart } from "@/modules/snapshots/snapshotEvents/components/statusPieChart";
import { MonthlyTrendChart } from "@/modules/snapshots/snapshotEvents/components/monthBarChart";
import { OverdueAlertPanel } from "@/modules/snapshots/snapshotEvents/components/overdueAlertPanel";
import { ActionTakerWorkloadChart } from "@/modules/snapshots/snapshotEvents/components/actionTakerWorkloadChart";
import { Loader } from "@/components/ui/loader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { EventTooltip } from "@/modules/snapshots/snapshotEvents/components/EventTooltip";
import { SortDropdown } from "@/components/sortDropdown";

export interface FiltersState {
  actionTaker: string | null;
  status: string | null;
  creator: string | null;
  collection: string | null;
  documentName: string | null;
  startDate: string | null;
  endDate: string | null;
  event_type: string | null;

  sort_field: "name" | "created_at";
  sort_order: "asc" | "desc";
}

export const SnapshotEventAnalytics = () => {
  const [hoveredEvent, setHoveredEvent] = React.useState<GetSnapshotEventGroupedResponseItem | null>(null);
  const [isReady, setIsReady] = React.useState(false);
  const [filters, setFilters] = React.useState<FiltersState>({
    actionTaker: "",
    status: "",
    creator: "",
    collection: "",
    documentName: "",
    startDate: "",
    endDate: "",
    event_type: "",
    sort_field: "name",
    sort_order: "asc",
  });

  const chartRef = React.useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  const getSnapshotEventGroupedFiltersQuery = useGetSnapshotEventGroupedFiltersQuery();
  const usersQuery = useGetDirectoryUsersQuery();
  const getSnapshotEventGroupedQuery = useGetSnapshotEventGroupedQuery({
    document_name: filters.documentName || undefined,
    status: filters.status || undefined,
    snapshot_collection_id: filters.collection || undefined,
    prompt_id: filters.event_type || undefined,
    event_date_gte: filters.endDate || undefined,
    event_date_lte: filters.startDate || undefined,
    action_taker: filters.actionTaker || undefined,
    creator: filters.creator || undefined,
    without_event: filters.event_type === WITHOUT_EVENT_FILTER_KEY ? true : undefined,
    sort_field: filters.sort_field,
    sort_order: filters.sort_order,
  });

  const getStatusColor = (status: string) => {
    if (status === "overdue") {
      return "#dc2626"; //danger
    }
    if (status === "done") {
      return "#16a34a"; // success
    }
    return "#3b82f6"; // info
  };

  const handleMouseEnter = (
    event: GetSnapshotEventGroupedResponseItem,
    mouseEvent: React.MouseEvent<SVGGElement, MouseEvent>,
  ) => {
    if (hoveredEvent || !chartRef.current) {
      return;
    }
    setHoveredEvent(event);
    const rect = chartRef.current.getBoundingClientRect();
    setTooltipPosition({
      x: mouseEvent.clientX - rect.left + 10,
      y: mouseEvent.clientY - rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setHoveredEvent(null);
  };

  const users = React.useMemo(() => {
    if (!usersQuery.data || !getSnapshotEventGroupedFiltersQuery.data) {
      return [];
    }

    return usersQuery.data.filter(user => getSnapshotEventGroupedFiltersQuery.data?.creator_ids.includes(user.id));
  }, [usersQuery, getSnapshotEventGroupedFiltersQuery]);

  const [chartWidth, setChartWidth] = React.useState(1000);
  React.useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.getBoundingClientRect().width);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  React.useEffect(() => {
    if (isReady && chartRef.current) {
      setChartWidth(chartRef.current.getBoundingClientRect().width);
    }
  }, [isReady]);

  if (getSnapshotEventGroupedQuery.isLoading || getSnapshotEventGroupedFiltersQuery.isLoading || usersQuery.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="h-8 w-8" />
      </div>
    );
  }

  if (
    !getSnapshotEventGroupedQuery.isSuccess ||
    !getSnapshotEventGroupedFiltersQuery.isSuccess ||
    !usersQuery.isSuccess
  ) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load data.</AlertDescription>
      </Alert>
    );
  }

  if (!isReady) {
    setIsReady(true);
  }

  const leftMargin = 500;
  const rightMargin = 50;
  const timelineWidth = Math.max(100, chartWidth - leftMargin - rightMargin); // Ensure positive width

  const getDatePosition = (dateStr: string) => {
    const date = new Date(dateStr);
    const startDate = getSnapshotEventGroupedQuery.data.metadata.startDate;
    const endDate = getSnapshotEventGroupedQuery.data.metadata.endDate;
    const totalDays = (+endDate - +startDate) / (1000 * 60 * 60 * 24);
    if (totalDays === 0) {
      return leftMargin;
    }
    const daysSinceStart = Math.abs(+date - +startDate) / (1000 * 60 * 60 * 24);

    return leftMargin + (daysSinceStart / totalDays) * timelineWidth;
  };

  const chartHeight = Object.keys(getSnapshotEventGroupedQuery.data.data).length * 75;

  return (
    <div>
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        actionTakers={getSnapshotEventGroupedFiltersQuery.data.recipients}
        creators={[...users]}
        collections={[{ name: "All collections", id: null }, ...getSnapshotEventGroupedFiltersQuery.data.collections]}
        eventTypes={[
          { name: "All event types", id: null },
          { name: "Collection", id: WITHOUT_EVENT_FILTER_KEY },
          ...getSnapshotEventGroupedFiltersQuery.data.prompts,
        ]}
      />
      <div className="mt-5 min-h-screen">
        <div className="mb-4 flex max-w-full gap-2">
          <StatusPieChart groupedEvents={getSnapshotEventGroupedQuery.data.data} getStatusColor={getStatusColor} />
          <ActionTakerWorkloadChart
            groupedEvents={getSnapshotEventGroupedQuery.data.data}
            getStatusColor={getStatusColor}
          />
          <MonthlyTrendChart groupedEvents={getSnapshotEventGroupedQuery.data.data} getStatusColor={getStatusColor} />
          <OverdueAlertPanel groupedEvents={getSnapshotEventGroupedQuery.data.data} getStatusColor={getStatusColor} />
        </div>
        <div
          ref={chartRef}
          className="relative box-border w-full rounded-lg border border-border bg-white p-3 shadow-lg"
        >
          <div className="ml-5 mt-2 flex">
            <SortDropdown
              fields={[
                { key: "created_at", label: "Created Date" },
                { key: "name", label: "Name" },
              ]}
              currentSort={{
                field: filters.sort_field,
                direction: filters.sort_order,
              }}
              onSortChange={({ field, direction }) =>
                setFilters({ ...filters, sort_field: field, sort_order: direction })
              }
            />
          </div>
          <TimelineChart
            chartWidth={chartWidth}
            chartHeight={chartHeight}
            leftMargin={leftMargin}
            timelineWidth={timelineWidth}
            data={getSnapshotEventGroupedQuery.data.data}
            getDatePosition={getDatePosition}
            monthMarkers={getSnapshotEventGroupedQuery.data.metadata.monthMarkers}
            getStatusColor={getStatusColor}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            hoveredEvent={hoveredEvent}
          />
          {hoveredEvent && (
            <EventTooltip hoveredEvent={hoveredEvent} tooltipPosition={tooltipPosition} users={usersQuery.data} />
          )}
        </div>
      </div>
    </div>
  );
};
